import * as React from 'react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { api } from './api';
import { defaultSyncConfig } from './plannerDefaults';
import { usePlannerActions } from './PlannerActionContext';
import version from './version.json';

function normalizeDetail(detail) {
  return {
    ...detail,
    budget: {
      ...detail.budget,
      currency_code: detail.budget.currency_code || 'RUB',
      notes: detail.budget.notes || '',
    },
    items: (detail.items || []).map((item, index) => ({
      ...item,
      amount: item.amount ?? '0',
      month: item.month ?? null,
      sort_order: item.sort_order || index + 1,
      firefly_category_id: item.firefly_category_id ?? null,
      firefly_category_ids: item.firefly_category_ids || (item.firefly_category_id ? [item.firefly_category_id] : []),
      firefly_budget_id: item.firefly_budget_id ?? null,
      firefly_account_id: item.firefly_account_id ?? null,
      firefly_bill_id: item.firefly_bill_id ?? null,
      firefly_rule_ids: item.firefly_rule_ids || [],
      create_firefly_bill: Boolean(item.create_firefly_bill),
      sync_to_firefly: Boolean(item.sync_to_firefly),
    })),
    sync_config: {
      ...defaultSyncConfig,
      ...(detail.sync_config || {}),
    },
    sync_runs: detail.sync_runs || [],
  };
}

function buildSavePayload(detail) {
  return {
    id: detail.budget.id || 0,
    year: Number(detail.budget.year),
    currency_code: detail.budget.currency_code || 'RUB',
    notes: detail.budget.notes || '',
    sync_config: detail.sync_config,
    items: detail.items.map((item, index) => ({
      ...item,
      id: item.id || 0,
      amount: item.amount || '0',
      month: item.frequency === 'once' ? Number(item.month || 1) : null,
      firefly_category_id: item.firefly_category_ids.length > 0 ? Number(item.firefly_category_ids[0]) : null,
      firefly_category_ids: item.firefly_category_ids.map(Number),
      firefly_budget_id: item.firefly_budget_id ? Number(item.firefly_budget_id) : null,
      firefly_account_id: item.firefly_account_id ? Number(item.firefly_account_id) : null,
      firefly_bill_id: item.firefly_bill_id ? Number(item.firefly_bill_id) : null,
      firefly_rule_ids: (item.firefly_rule_ids || []).map(Number),
      create_firefly_bill: item.kind === 'one_time' ? true : Boolean(item.create_firefly_bill),
      sync_to_firefly: Boolean(item.sync_to_firefly),
      sort_order: index + 1,
    })),
  };
}

export default function AdminWorkspace() {
  const { selectedYear, setSelectedYear, budgets, selectedBudgetId, refreshBudgets } = usePlannerActions();
  const [detail, setDetail] = React.useState(null);
  const [referenceData, setReferenceData] = React.useState({ accounts: [] });
  const [selectedAccountId, setSelectedAccountId] = React.useState('');
  const [accountTags, setAccountTags] = React.useState(null);
  const [selectedTag, setSelectedTag] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [accountBusy, setAccountBusy] = React.useState(false);
  const [restoringRunId, setRestoringRunId] = React.useState(null);
  const [oldBudgetId, setOldBudgetId] = React.useState('');
  const [newBudgetId, setNewBudgetId] = React.useState('');
  const [deactivateResult, setDeactivateResult] = React.useState(null);
  const [deletingTag, setDeletingTag] = React.useState('');
  const [deletingAllTag, setDeletingAllTag] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const refs = await api.getPlannerReference();
        if (cancelled) return;
        setReferenceData(refs || { accounts: [] });
        const accounts = refs?.accounts || [];
        const assetAccount = accounts.find((account) => account.attributes?.type === 'asset');
        if (assetAccount) {
          setSelectedAccountId(assetAccount.id);
        } else if (accounts.length > 0) {
          setSelectedAccountId(accounts[0].id);
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!selectedBudgetId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    async function loadBudget() {
      try {
        setLoading(true);
        const budgetDetail = await api.getPlannerBudget(selectedBudgetId);
        if (!cancelled) {
          setDetail(normalizeDetail(budgetDetail));
          setError('');
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    loadBudget();
    return () => {
      cancelled = true;
    };
  }, [selectedBudgetId]);

  function updateSyncConfig(field, value) {
    setDetail((current) => ({
      ...current,
      sync_config: {
        ...current.sync_config,
        [field]: value,
      },
    }));
  }

  async function refreshBudget(id) {
    await refreshBudgets();
    const budgetDetail = await api.getPlannerBudget(id);
    setDetail(normalizeDetail(budgetDetail));
  }

  async function saveSettings() {
    if (!detail) {
      return;
    }
    try {
      setBusy(true);
      setMessage('');
      setError('');
      const saved = await api.savePlannerBudget(buildSavePayload(detail));
      setDetail(normalizeDetail(saved));
      setMessage('Настройки синхронизации сохранены.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setBusy(false);
    }
  }

  async function syncBudget() {
    if (!detail) {
      return;
    }
    try {
      setBusy(true);
      setMessage('Сохраняю настройки перед синхронизацией...');
      setError('');
      const saved = await api.savePlannerBudget(buildSavePayload(detail));
      await api.syncPlannerBudget(saved.budget.id, { sync_config: saved.sync_config });
      if (selectedAccountId) {
        await loadAccountTags(selectedAccountId, false);
      }
      await refreshBudget(saved.budget.id);
      setMessage('Синхронизация завершена.');
    } catch (syncError) {
      setError(syncError.message);
    } finally {
      setBusy(false);
    }
  }

  async function loadAccountTags(accountId = selectedAccountId, showMessage = true) {
    if (!accountId) {
      setError('Выберите счет Firefly III.');
      return;
    }
    try {
      setAccountBusy(true);
      if (showMessage) {
        setMessage('');
      }
      setError('');
      const summary = await api.getPlannerAccountTags(accountId);
      setAccountTags(summary);
      if (!summary.tags?.some((tag) => tag.name === selectedTag)) {
        setSelectedTag(summary.tags?.[0]?.name || '');
      }
      if (showMessage) {
        setMessage(`Информация по счету "${summary.account?.attributes?.name || accountId}" обновлена.`);
      }
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setAccountBusy(false);
    }
  }

  async function deleteTaggedTransactions(tagName = selectedTag) {
    if (!selectedAccountId || !tagName) {
      setError('Выберите счет и метку для удаления.');
      return;
    }
    try {
      setDeletingTag(tagName);
      setMessage('');
      setError('');
      const result = await api.deletePlannerTaggedTransactions(selectedAccountId, {
        tag: tagName,
        batch_size: 50,
      });
      setAccountTags(result.summary);
      if (!result.summary.tags?.some((tag) => tag.name === tagName)) {
        setSelectedTag(result.summary.tags?.[0]?.name || '');
      }
      setMessage(`Удалено ${result.deleted_count} транзакций с меткой "${tagName}". Осталось: ${result.remaining_count}.`);
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeletingTag('');
    }
  }

  async function deleteAllTaggedTransactions(tagName = selectedTag) {
    if (!selectedAccountId || !tagName) {
      setError('Выберите счет и метку для удаления.');
      return;
    }
    try {
      setDeletingAllTag(tagName);
      setMessage('');
      setError('');
      let totalDeleted = 0;
      let remainingCount = null;

      for (;;) {
        const result = await api.deletePlannerTaggedTransactions(selectedAccountId, {
          tag: tagName,
          batch_size: 50,
        });
        totalDeleted += result.deleted_count;
        remainingCount = result.remaining_count;
        setAccountTags(result.summary);
        setMessage(`Удаляю метку "${tagName}": удалено ${totalDeleted}, осталось ${remainingCount}.`);

        if (result.deleted_count === 0 || result.remaining_count === 0) {
          if (!result.summary.tags?.some((tag) => tag.name === tagName)) {
            setSelectedTag(result.summary.tags?.[0]?.name || '');
          }
          break;
        }
      }

      setMessage(`Удаление метки "${tagName}" завершено. Удалено ${totalDeleted}. Осталось: ${remainingCount || 0}.`);
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setDeletingAllTag('');
    }
  }

  async function exportBudget() {
    if (!detail) {
      return;
    }
    try {
      setBusy(true);
      setMessage('');
      setError('');
      const blob = await api.exportPlannerBudget(detail.budget.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `budget-${detail.budget.year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setMessage('Экспорт XLSX подготовлен.');
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setBusy(false);
    }
  }

  async function importBudget() {
    if (!file) {
      setError('Выберите XLSX файл для импорта.');
      return;
    }
    try {
      setBusy(true);
      setMessage('');
      setError('');
      const imported = await api.importPlannerBudget(file, selectedBudgetId);
      const normalized = normalizeDetail(imported);
      setDetail(normalized);
      setSelectedYear(normalized.budget.year);
      setFile(null);
      await refreshBudgets();
      setMessage(`Импортирован бюджет ${normalized.budget.year}.`);
    } catch (importError) {
      setError(importError.message);
    } finally {
      setBusy(false);
    }
  }

  async function restoreRun(runId) {
    try {
      setRestoringRunId(runId);
      setMessage('');
      setError('');
      const restored = await api.restorePlannerRun(runId);
      await refreshBudget(restored.budget_id || detail.budget.id);
      setMessage(`Восстановление из sync run #${runId} завершено.`);
    } catch (restoreError) {
      setError(restoreError.message);
    } finally {
      setRestoringRunId(null);
    }
  }

  async function handleDeactivateSubscriptions() {
    if (!oldBudgetId || !newBudgetId) {
      return;
    }
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const result = await api.deactivateSubscriptions({
        old_budget_id: Number(oldBudgetId),
        new_budget_id: Number(newBudgetId),
      });
      setDeactivateResult(result);
      setMessage(`Деактивировано подписок: ${result.deactivated_count}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const syncStats = React.useMemo(() => {
    const items = detail?.items || [];
    return {
      all: items.length,
      enabled: items.filter((item) => item.sync_to_firefly).length,
      bills: items.filter((item) => item.create_firefly_bill).length,
    };
  }, [detail]);

  if (loading && !detail) {
    return (
      <main className="planner-page">
        <section className="hero-card">
          <p className="eyebrow">Admin</p>
          <h1>Загружаю администрирование...</h1>
        </section>
      </main>
    );
  }

  return (
    <main className="planner-page admin-page">
      <section className="hero-card admin-hero">
        <div>
          <p className="eyebrow">Admin Console</p>
          <h1>Администрирование бюджета</h1>
          <p className="hero-copy">
            Здесь живут опасные переключатели sync, журнал восстановления и импорт/экспорт XLSX. Планирование осталось
            отдельно, чтобы случайно не дернуть удаление данных в Firefly.
          </p>
          <p className="version-info">
            Версия: {version.version}
          </p>
        </div>
        <div className="hero-actions">
          <label className="field compact-field">
            <span>Год</span>
            <select value={selectedYear} onChange={(event) => setSelectedYear(Number(event.target.value))}>
              <option value={0}>Новый из файла</option>
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.year}>
                  {budget.year}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {(message || error) && <section className={`message-banner ${error ? 'error' : 'success'}`}>{error || message}</section>}

      <section className="stats-grid">
        <AdminStat title="Строк в плане" value={syncStats.all} />
        <AdminStat title="Sync включен" value={syncStats.enabled} />
        <AdminStat title="Подписок" value={syncStats.bills} />
        <AdminStat title="Последний режим" value={detail?.sync_config?.sync_mode || 'нет бюджета'} />
      </section>

      <section className="editor-grid">
        <article className="panel panel-accent">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Excel</p>
              <h2>Импорт и экспорт бюджета</h2>
            </div>
          </div>
          <div className="admin-grid">
            <button type="button" className="primary-button" onClick={exportBudget} disabled={!detail || busy}>
              <DownloadOutlinedIcon fontSize="small" /> Экспорт XLSX
            </button>
            <label className="field field-wide">
              <span>Файл для импорта</span>
              <input type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
            </label>
            <button type="button" className="ghost-button" onClick={importBudget} disabled={!file || busy}>
              <UploadFileOutlinedIcon fontSize="small" /> Импорт XLSX
            </button>
          </div>
          <p className="empty-state">
            Импорт заменяет строки выбранного бюджета строками из файла. Если бюджет не выбран, год берется из таблицы.
          </p>
        </article>

        <article className="panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Sync</p>
              <h2>Режимы синхронизации</h2>
            </div>
            <div className="panel-actions">
              <button type="button" className="ghost-button" onClick={saveSettings} disabled={!detail || busy}>
                <SaveOutlinedIcon fontSize="small" /> Сохранить
              </button>
              <button type="button" className="primary-button" onClick={syncBudget} disabled={!detail || busy}>
                <SyncIcon fontSize="small" /> Синхронизировать
              </button>
            </div>
          </div>
          {detail ? (
            <div className="admin-grid">
              <label className="field">
                <span>Режим budgets</span>
                <select value={detail.sync_config.sync_mode} onChange={(event) => updateSyncConfig('sync_mode', event.target.value)}>
                  <option value="replace_firefly">Planner — источник правды</option>
                  <option value="bidirectional">Bidirectional sync</option>
                </select>
              </label>
              <label className="field">
                <span>Режим правил</span>
                <select value={detail.sync_config.rule_sync_mode} onChange={(event) => updateSyncConfig('rule_sync_mode', event.target.value)}>
                  <option value="replace">Перезаписывать planner-часть правил</option>
                  <option value="merge">Только дополнять</option>
                </select>
              </label>
              <label className="field">
                <span>Подписки</span>
                <select
                  value={detail.sync_config.subscription_sync_mode}
                  onChange={(event) => updateSyncConfig('subscription_sync_mode', event.target.value)}
                >
                  <option value="keep">Не удалять лишние подписки</option>
                  <option value="replace_active">Удалять лишние активные подписки</option>
                </select>
              </label>
              <label className="switch-line">
                <input
                  type="checkbox"
                  checked={Boolean(detail.sync_config.delete_managed_budgets)}
                  onChange={(event) => updateSyncConfig('delete_managed_budgets', event.target.checked)}
                />
                <span>Удалять Firefly budgets, которых нет в плане выбранного года</span>
              </label>
              <label className="switch-line">
                <input
                  type="checkbox"
                  checked={Boolean(detail.sync_config.delete_active_subscriptions)}
                  onChange={(event) => updateSyncConfig('delete_active_subscriptions', event.target.checked)}
                />
                <span>Удалять активные Firefly subscriptions, которых нет в плане</span>
              </label>
            </div>
          ) : (
            <p className="empty-state">Выберите бюджет или импортируйте XLSX файл.</p>
          )}
        </article>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Firefly III</p>
            <h2>Счет, метки и пакетное удаление</h2>
          </div>
          <div className="panel-actions">
            <button type="button" className="primary-button" onClick={() => loadAccountTags()} disabled={!selectedAccountId || accountBusy || busy}>
              <SyncIcon fontSize="small" /> {accountBusy ? 'Обновляю...' : 'Синхронизировать счет'}
            </button>
          </div>
        </div>
        <div className="account-sync-grid">
          <label className="field">
            <span>Счет Firefly III</span>
            <select
              value={selectedAccountId}
              onChange={(event) => {
                setSelectedAccountId(event.target.value);
                setAccountTags(null);
                setSelectedTag('');
              }}
            >
              <option value="">Выберите счет</option>
              {(referenceData.accounts || []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.attributes?.name || account.id} ({account.attributes?.type || 'account'})
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Метка для удаления</span>
            <select value={selectedTag} onChange={(event) => setSelectedTag(event.target.value)} disabled={!accountTags?.tags?.length}>
              <option value="">Выберите метку</option>
              {(accountTags?.tags || []).map((tag) => (
                <option key={tag.name} value={tag.name}>
                  {tag.name} - {tag.count}
                </option>
              ))}
            </select>
          </label>
          <div className="batch-actions">
            <button
              type="button"
              className="ghost-button danger-button"
              onClick={() => deleteTaggedTransactions()}
              disabled={!selectedAccountId || !selectedTag || Boolean(deletingTag) || Boolean(deletingAllTag)}
            >
              <DeleteOutlinedIcon fontSize="small" /> Удалить 50
            </button>
            <button
              type="button"
              className="ghost-button danger-button"
              onClick={() => deleteAllTaggedTransactions()}
              disabled={!selectedAccountId || !selectedTag || Boolean(deletingTag) || Boolean(deletingAllTag)}
            >
              <DeleteOutlinedIcon fontSize="small" /> {deletingAllTag ? 'Удаляю все...' : 'Удалить все'}
            </button>
          </div>
        </div>
        <AccountTagsPanel
          summary={accountTags}
          selectedTag={selectedTag}
          deletingTag={deletingTag}
          deletingAllTag={deletingAllTag}
          onSelectTag={setSelectedTag}
          onDeleteTag={deleteTaggedTransactions}
        />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Подписки</p>
            <h2>Деактивация старых подписок</h2>
          </div>
        </div>
        <div className="admin-grid">
          <label className="field">
            <span>Старый бюджет</span>
            <select value={oldBudgetId} onChange={(e) => setOldBudgetId(e.target.value)}>
              <option value="">Выберите бюджет</option>
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.id}>
                  Бюджет {budget.year}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Новый бюджет</span>
            <select value={newBudgetId} onChange={(e) => setNewBudgetId(e.target.value)}>
              <option value="">Выберите бюджет</option>
              {budgets.map((budget) => (
                <option key={budget.id} value={budget.id}>
                  Бюджет {budget.year}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="primary-button"
            onClick={handleDeactivateSubscriptions}
            disabled={!oldBudgetId || !newBudgetId || busy}
          >
            Деактивировать старые подписки
          </button>
        </div>
        {deactivateResult && deactivateResult.deactivated_count > 0 && (
          <div className="table-scroll" style={{ marginTop: '1rem' }}>
            <table className="planner-table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Bill ID</th>
                </tr>
              </thead>
              <tbody>
                {deactivateResult.deactivated.map((sub) => (
                  <tr key={sub.item_id}>
                    <td>{sub.name}</td>
                    <td>{sub.bill_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {deactivateResult && deactivateResult.deactivated_count === 0 && (
          <p className="empty-state">Нет подписок для деактивации.</p>
        )}
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <p className="panel-kicker">Журнал</p>
            <h2>История синхронизаций и восстановление</h2>
          </div>
        </div>
        <SyncRunsPanel runs={detail?.sync_runs || []} restoringRunId={restoringRunId} onRestore={restoreRun} />
      </section>
    </main>
  );
}

function AdminStat({ title, value }) {
  return (
    <article className="stat-card neutral">
      <p>{title}</p>
      <strong>{value}</strong>
    </article>
  );
}

function AccountTagsPanel({ summary, selectedTag, deletingTag, deletingAllTag, onSelectTag, onDeleteTag }) {
  if (!summary) {
    return <p className="empty-state">Выберите счет и нажмите синхронизацию, чтобы загрузить метки по транзакциям этого счета.</p>;
  }

  return (
    <div className="account-tags-panel">
      <div className="account-summary">
        <AdminStat title="Счет" value={summary.account?.attributes?.name || summary.account?.id || '-'} />
        <AdminStat title="Транзакций" value={summary.total_transactions} />
        <AdminStat title="Меток" value={summary.tags?.length || 0} />
        <AdminStat title="Без меток" value={summary.untagged_transactions} />
      </div>
      {summary.tags?.length > 0 ? (
        <div className="table-scroll">
          <table className="planner-table account-tags-table">
            <thead>
              <tr>
                <th>Метка</th>
                <th>Транзакций</th>
                <th>Последняя</th>
                <th>Действие</th>
              </tr>
            </thead>
            <tbody>
              {summary.tags.map((tag) => (
                <tr key={tag.name} className={selectedTag === tag.name ? 'selected-row' : ''}>
                  <td>
                    <button type="button" className="tag-select-button" onClick={() => onSelectTag(tag.name)}>
                      {tag.name}
                    </button>
                  </td>
                  <td>{tag.count}</td>
                  <td>
                    <span>{tag.latest_date ? formatTimestamp(tag.latest_date) : '-'}</span>
                    {tag.last_description && <small className="muted-cell">{tag.last_description}</small>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="ghost-button danger-button"
                      onClick={() => onDeleteTag(tag.name)}
                      disabled={Boolean(deletingTag) || Boolean(deletingAllTag)}
                    >
                      <DeleteOutlinedIcon fontSize="small" /> {deletingTag === tag.name ? 'Удаляю...' : 'Удалить 50'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="empty-state">Для выбранного счета метки не найдены.</p>
      )}
    </div>
  );
}

function SyncRunsPanel({ runs, restoringRunId, onRestore }) {
  if (!runs.length) {
    return <p className="empty-state">История sync пока пуста.</p>;
  }
  return (
    <div className="sync-runs">
      {runs.map((run) => (
        <article key={run.id} className="sync-run-card">
          <div className="sync-run-head">
            <div>
              <strong>Run #{run.id}</strong>
              <p>{run.summary || run.status}</p>
            </div>
            <div className="sync-run-meta">
              <span>{run.mode}</span>
              <span>{run.status}</span>
              <span>{formatTimestamp(run.started_at)}</span>
            </div>
          </div>
          {run.logs?.length > 0 && (
            <ul className="sync-log-list">
              {run.logs.map((entry, index) => (
                <li key={`${run.id}-${index}`}>
                  <strong>{entry.action}</strong>
                  <span>{entry.message}</span>
                  <small>{formatTimestamp(entry.time)}</small>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="ghost-button" onClick={() => onRestore(run.id)} disabled={restoringRunId === run.id}>
            <RestoreOutlinedIcon fontSize="small" /> {restoringRunId === run.id ? 'Восстанавливаю...' : 'Восстановить'}
          </button>
        </article>
      ))}
    </div>
  );
}

function formatTimestamp(value) {
  if (!value) {
    return '';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
